import { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import type {
    DigestConfig,
    DigestPreviewSummary,
} from '@/types/communications'
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Field,
    Input,
    LoadingPanel,
    PillButton,
    SectionTitle,
    Toggle,
} from './_shared'
import {
    Send,
    Save,
    Inbox,
    PlayCircle,
    MessageSquare,
    Video,
    Workflow,
    BarChart3,
    Boxes,
} from 'lucide-react'

type BlockKey = keyof DigestConfig['blocks']

const BLOCKS: Array<{
    key: BlockKey
    label: string
    icon: React.ComponentType<{ className?: string }>
}> = [
    { key: 'chat', label: 'Chat', icon: MessageSquare },
    { key: 'meetings', label: 'Meetings', icon: Video },
    { key: 'workflows', label: 'Workflows', icon: Workflow },
    { key: 'arc', label: 'ARC', icon: BarChart3 },
    { key: 'apps', label: 'Libra Apps', icon: Boxes },
]

export function DigestsTab() {
    const [config, setConfig] = useState<DigestConfig | null>(null)
    const [draft, setDraft] = useState<DigestConfig | null>(null)
    const [saving, setSaving] = useState(false)
    const [preview, setPreview] = useState<DigestPreviewSummary | null>(null)
    const [previewBusy, setPreviewBusy] = useState(false)
    const [sweepResult, setSweepResult] = useState<string | null>(null)
    const [sendBusy, setSendBusy] = useState(false)

    useEffect(() => {
        adminApi.getDigestConfig().then(c => {
            setConfig(c)
            // Drop any legacy template pinning on load — digests now always use
            // the built-in programmatic layout. Saving once will clear the
            // fields on the server too.
            setDraft({ ...c, templateId: null, slotValues: null })
        })
    }, [])

    if (!draft || !config) {
        return <LoadingPanel label="Loading digest config…" />
    }

    const dirty = JSON.stringify(draft) !== JSON.stringify(config)

    const save = async () => {
        setSaving(true)
        try {
            // Force-null the legacy template fields so saved config always
            // uses the programmatic layout.
            const payload = { ...draft, templateId: null, slotValues: null }
            await adminApi.updateDigestConfig(payload)
            setConfig(payload)
            setDraft(payload)
        } finally {
            setSaving(false)
        }
    }

    const runPreview = async () => {
        setPreviewBusy(true)
        try {
            const result = await adminApi.previewDigest({ windowDays: 7 })
            setPreview(result.summary)
        } finally {
            setPreviewBusy(false)
        }
    }

    const sendToMe = async () => {
        setSendBusy(true)
        try {
            const r = await adminApi.sendDigestToMe()
            setSweepResult(
                r.sent ? 'Sent — check your inbox' : `Skipped: ${r.reason ?? 'unknown'}`
            )
        } finally {
            setSendBusy(false)
        }
    }

    const runSweep = async () => {
        setSendBusy(true)
        try {
            const r = await adminApi.runDigestSweep()
            setSweepResult(
                `daily=${r.daySent} · weekly=${r.weekSent} · skipped=${r.skipped}`
            )
        } finally {
            setSendBusy(false)
        }
    }

    return (
        <div className="space-y-6">
            <SectionTitle
                title="Digests"
                description="Auto-generated activity summary. Sent in each user's local morning window with their own chat / meeting / workflow / ARC / Libra-Apps counts. No manual copy to write."
                actions={
                    <PillButton
                        variant="primary"
                        onClick={save}
                        disabled={!dirty || saving}
                    >
                        <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save'}
                    </PillButton>
                }
            />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="space-y-4 lg:col-span-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="!text-sm">Status</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Toggle
                                checked={draft.enabled}
                                onChange={v => setDraft({ ...draft, enabled: v })}
                                label={draft.enabled ? 'Digests are on' : 'Digests are off'}
                                description={
                                    draft.enabled
                                        ? 'Eligible users will receive a digest in their local morning window.'
                                        : 'No digests will be sent regardless of user preferences.'
                                }
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Field
                                label="Subject template"
                                hint={
                                    "Use {frequency} — replaced with 'day' or 'week'."
                                }
                            >
                                <Input
                                    value={draft.subjectTemplate}
                                    onChange={e =>
                                        setDraft({
                                            ...draft,
                                            subjectTemplate: e.target.value,
                                        })
                                    }
                                    placeholder="Your {frequency} Libra digest"
                                />
                            </Field>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="!text-sm">Content blocks</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="mb-4 text-xs text-gray-500">
                                Each block is included only when the user has activity in
                                that area.
                            </p>
                            <div className="space-y-3">
                                {BLOCKS.map(b => {
                                    const Icon = b.icon
                                    return (
                                        <div
                                            key={b.key}
                                            className="flex items-center gap-3"
                                        >
                                            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-600">
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <Toggle
                                                checked={draft.blocks[b.key]}
                                                onChange={v =>
                                                    setDraft({
                                                        ...draft,
                                                        blocks: {
                                                            ...draft.blocks,
                                                            [b.key]: v,
                                                        },
                                                    })
                                                }
                                                label={b.label}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="!text-sm">Preview window</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <p className="text-xs text-gray-500">
                                Aggregates your activity for the last 7 days using the
                                live aggregator. Doesn't send anything.
                            </p>
                            <PillButton
                                variant="default"
                                onClick={runPreview}
                                disabled={previewBusy}
                            >
                                <PlayCircle className="h-3.5 w-3.5" />
                                {previewBusy ? 'Running…' : 'Run preview'}
                            </PillButton>
                            {preview && (
                                <div className="space-y-2 border-t border-gray-100 pt-3 text-xs">
                                    <PreviewRow
                                        label="Chat messages"
                                        value={preview.chatMessageCount}
                                    />
                                    <PreviewRow
                                        label="Meetings"
                                        value={preview.meetingsCount}
                                    />
                                    <PreviewRow
                                        label="Workflows"
                                        value={`${preview.workflowRuns.ok} ok · ${preview.workflowRuns.failed} failed`}
                                    />
                                    <PreviewRow
                                        label="ARC reports"
                                        value={preview.arcReportsCount}
                                    />
                                    <PreviewRow
                                        label="Apps deployed"
                                        value={preview.appsDeployedCount}
                                    />
                                    {preview.lastSummaries.length > 0 && (
                                        <div className="border-t border-gray-100 pt-2">
                                            <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                                Recent meetings
                                            </p>
                                            <ul className="mt-1 space-y-0.5 text-xs text-gray-900">
                                                {preview.lastSummaries.map((s, i) => (
                                                    <li key={i}>
                                                        {s.title}
                                                        {s.subhead && (
                                                            <span className="text-gray-500">
                                                                {' '}
                                                                · {s.subhead}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="!text-sm">Manual</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <p className="text-xs text-gray-500">
                                Trigger a real send to yourself or run the sweep ahead of
                                schedule.
                            </p>
                            <PillButton
                                variant="primary"
                                onClick={sendToMe}
                                disabled={sendBusy}
                            >
                                <Send className="h-3.5 w-3.5" /> Send digest to me now
                            </PillButton>
                            <PillButton
                                variant="default"
                                onClick={runSweep}
                                disabled={sendBusy}
                            >
                                <Inbox className="h-3.5 w-3.5" /> Run sweep now
                            </PillButton>
                            {sweepResult && (
                                <div className="rounded border border-gray-200 bg-gray-50 p-2 font-mono text-xs text-gray-700">
                                    {sweepResult}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    )
}

function PreviewRow({
    label,
    value,
}: {
    label: string
    value: number | string
}) {
    return (
        <div className="flex justify-between">
            <span className="text-gray-500">{label}</span>
            <span className="font-medium tabular-nums text-gray-900">{value}</span>
        </div>
    )
}
